import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { chatRepository } from '../api/chat.repository';

export const chatKeys = {
  all: ['chat'] as const,
  messages: (applicationId: string) => [...chatKeys.all, applicationId] as const,
};

/** Chat messages for an application, kept live via a Realtime subscription. */
export function useChatMessages(applicationId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: chatKeys.messages(applicationId),
    queryFn: () => unwrap(chatRepository.listForApplication(applicationId)),
    enabled: Boolean(applicationId),
  });

  useEffect(() => {
    if (!applicationId) return;
    const unsubscribe = chatRepository.subscribe(applicationId, () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(applicationId) });
    });
    return unsubscribe;
  }, [applicationId, queryClient]);

  return query;
}

export function useSendMessage(applicationId: string) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (body: string) =>
      unwrap(chatRepository.send(applicationId, userId as string, body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(applicationId) });
    },
  });
}
